import { Facade } from "@/drawing";
import { drawingImageRepository } from "@/repositories/drawingImage.repository";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { Logger } from "@/logger";

/**
 * Rouvre le plan affiché sur un nouveau DWG (résultat d'une fusion de versions,
 * restauration d'une sauvegarde), sans quitter l'outil dessin.
 *
 * `Facade.file.open` seul ne suffit pas : il faut rejouer les étapes de
 * l'ouverture initiale qui le suivent (cf. DrawingCanvas.initializeViewer) —
 * images de fond, épaisseurs de trait, puis `resize()` et premier rendu. Sans
 * `resize()`, le nouvel affichage n'a pas de taille, chaque rendu lève une
 * exception ODA et le canvas reste figé, puis devient noir.
 *
 * Tout ce qui attend (IndexedDB) est fait AVANT de fermer le plan : entre
 * l'ouverture et `resize()`, la boucle de rendu (`DRDrawingViewer.animate`)
 * ne doit avoir aucune image à dessiner sur un affichage sans taille.
 */
export async function reopenDrawing(
  drawingId: number,
  dwg: ArrayBuffer,
): Promise<void> {
  if (!Facade.isInitialized) return;
  const images = await drawingImageRepository.getByDrawingId(drawingId);
  const preparedImages = images
    .filter((img) => img.data)
    .map((img) => ({ path: img.rasterPath, array: img.data }));

  // Annuler / rétablir viseraient des objets de l'ancien plan : on vide
  // l'historique tant que ces objets existent encore (avant la fermeture).
  Facade.history.clear();
  Facade.file.close();
  await Facade.file.open(`drawing_${drawingId}.dwg`, dwg);
  Facade.view.resize();

  Facade.images.loadReferencedImages(preparedImages);
  Facade.images.reloadImageDefs();
  try {
    Facade.setLineweightDisplayInitial(DraggerMenuState.isLwActive);
  } catch (e) {
    // Les épaisseurs de trait se réappliquent au prochain réglage.
    Logger.captureException(e, { site: "reopenDrawing.lineweight", drawingId });
  }
  Facade.view.update();
  Facade.view.regenIfNeeded();
}
